import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { FileText, ImageIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface AssetRecordAttachmentsProps {
  assetId: number;
}

interface AssetRecord {
  id: number;
  title: string;
  images: string | string[] | null;
  documents: string | string[] | null;
}

interface PreviewState {
  isOpen: boolean;
  type: 'image' | 'document';
  url: string;
  title: string;
}

// Helper function to process attachment string or array
const processAttachments = (value: string | string[] | null): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return value.split(',').filter(Boolean);
};

export function AssetRecordAttachments({ assetId }: AssetRecordAttachmentsProps) {
  const { t } = useTranslation();
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);

  const { data: records, isLoading } = useQuery({
    queryKey: ["asset-record-attachments", assetId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from('asset_records')
        .select(`
          id,
          title,
          images,
          documents,
          assets!asset_records_asset_id_fkey (
            asset_name,
            name
          )
        `)
        .eq("asset_id", assetId)
        .not('images', 'is', null)
        .not('documents', 'is', null)
        .order('title', { ascending: true });

      if (error) throw error;

      // Add debug logging
      console.log('Raw data from query:', data);

      // Filter out records with empty attachments
      const filteredRecords = (data || []).filter(record => {
        const hasImages = record.images && processAttachments(record.images).length > 0;
        const hasDocuments = record.documents && processAttachments(record.documents).length > 0;
        return hasImages || hasDocuments;
      });

      // Add debug logging
      console.log('Filtered records:', filteredRecords);

      return filteredRecords;
    }
  });

  if (isLoading) return null;
  if (!records || records.length === 0) return null;

  return (
    <div className="space-y-6">
      <Card className="p-6 space-y-6 bg-white">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {t('asset.recordAttachments')}
        </h2>

        <div className="space-y-8">
          {records.map((record) => {
            const images = processAttachments(record.images);
            const documents = processAttachments(record.documents);

            return (
              <div key={record.id} className="space-y-4">
                <h3 className="text-md font-medium text-gray-900">{record.title}</h3>

                {/* Images Grid */}
                {images.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <ImageIcon className="h-4 w-4" />
                      <span>{t('records.images')} ({images.length})</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {images.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setPreviewState({
                            isOpen: true,
                            type: 'image',
                            url: image,
                            title: `${record.title} - ${t('records.image')} ${index + 1}`
                          })}
                          className="group aspect-square relative rounded-lg overflow-hidden border border-gray-200 hover:border-apple-blue transition-colors"
                        >
                          <img
                            src={image}
                            alt={`${record.title} - ${t('records.image')} ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents List */}
                {documents.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="h-4 w-4" />
                      <span>{t('records.documents')} ({documents.length})</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {documents.map((doc, index) => (
                        <button
                          key={index}
                          onClick={() => setPreviewState({
                            isOpen: true,
                            type: 'document',
                            url: doc,
                            title: `${record.title} - ${t('records.document')} ${index + 1}`
                          })}
                          className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-apple-blue transition-colors group"
                        >
                          <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-gray-600 group-hover:text-apple-blue transition-colors" />
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <p className="font-medium text-gray-900 truncate group-hover:text-apple-blue transition-colors">
                              {t('records.document')} {index + 1}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              {record.title}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewState?.isOpen} onOpenChange={(open) => !open && setPreviewState(null)}>
        <DialogContent className="sm:max-w-[800px] h-[90vh] flex flex-col">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>{previewState?.title}</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPreviewState(null)}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto flex items-center justify-center p-4">
            {previewState?.type === 'image' ? (
              <img
                src={previewState.url}
                alt={previewState.title}
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <iframe
                src={previewState?.url}
                title={previewState?.title}
                className="w-full h-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 